import { useMemo } from 'react';
import { useUserActiveStatus, useUserLoggedIn } from '~/modules/user';
import type { Conversation } from '../types';
import {
    getChatParticipantIdentitiesExcludingSelf,
    getEnquiryConversationEnquirer,
    isEnquiryProfessionalSide,
} from '../utils';

/**
 * Hook to get the active status of a conversation. This depends on the
 * inbox context, conversation type and participants.
 * @param conversation The conversation to get the active status for.
 * @param professionalID The ID of the professional if in a company inbox context.
 * @returns The active status of the conversation.
 */
export function useConversationActiveStatus(
    conversation: Conversation | null | undefined,
    professionalID?: number | null,
) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const isDirectConversation =
        conversation?.__typename === 'DirectConversation';
    const isGroupChat = isDirectConversation && conversation.IsGroupChat;
    const otherParticipants = useMemo(() => {
        return getChatParticipantIdentitiesExcludingSelf(
            conversation,
            isLoggedIn ? user.ID : 0,
            professionalID,
        );
    }, [conversation, isLoggedIn, user.ID, professionalID]);
    const memberIDs = useMemo(() => {
        if (!conversation) {
            return null;
        }
        if (isDirectConversation) {
            return otherParticipants
                .filter((participant) => participant.__typename === 'Member')
                .map((participant) => participant.ID);
        }
        if (
            isEnquiryProfessionalSide(
                conversation,
                isLoggedIn ? user.ID : 0,
                professionalID,
            )
        ) {
            const enquirer = getEnquiryConversationEnquirer(conversation);
            if (enquirer) {
                return [enquirer.ID];
            }
        }
        return null;
    }, [
        conversation,
        isDirectConversation,
        otherParticipants,
        isLoggedIn,
        user.ID,
        professionalID,
    ]);
    const userStatuses = useUserActiveStatus(memberIDs);

    return useMemo(() => {
        if (!conversation) {
            return null;
        }
        // A group chat status is 'active' if at least one member is active.
        if (isGroupChat) {
            return Object.values(userStatuses).some(
                (status) => status === 'active',
            )
                ? 'active'
                : 'inactive';
        }
        // A 1-on-1 chat or enquiry conversation status is the status of the
        // other participant (member only).
        return memberIDs?.[0] ? (userStatuses[memberIDs[0]] ?? null) : null;
    }, [conversation, isGroupChat, userStatuses, memberIDs]);
}
